import React, { useState, useRef, useEffect } from "react";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../NavBars/ActionToolbar";
import SherComboBox from "../../ComboBoxes/SherComboBox";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import io from "socket.io-client";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectMenuPermissions } from "../../../app/permissionsSlice";
import { selectActiveLocation } from "../../../app/locationSlice";
import { useDbContext } from "../../../context/DbContext";
import { useActiveFyLimits } from "../../../utils/fyDateValidation";

function SchemeRegistration({ onClose, Open, OpenSchemeMgmt, zIndex, actionPermissions, initialAccountName, initialScheme }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const permissions = useSelector(selectMenuPermissions);
  const activeLocation = useSelector(selectActiveLocation);

  const [minimized, setMinimized] = useState(false);
  const [dataFound, setDataFound] = useState(false);
  const [editDisabled, setEditDisabled] = useState(true);
  const [deleteDisabled, setDeleteDisabled] = useState(true);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [hasReceipts, setHasReceipts] = useState(false);
  const [originalAccountInfo, setOriginalAccountInfo] = useState({ accountName: null, schemeName: null, accountNo: "" });
  const [activeTab, setActiveTab] = useState('address');
  const [proofFrontPreview, setProofFrontPreview] = useState(null);
  const [proofBackPreview, setProofBackPreview] = useState(null);

  const checkAccountReceipts = async (accId, accNo, autoId, schemeId) => {
    setOriginalAccountInfo({
      accountName: accId !== undefined ? accId : null,
      schemeName: schemeId !== undefined ? schemeId : null,
      accountNo: String(accNo || "").trim(),
    });
    try {
      if (!accId && !accNo && !autoId) {
        setHasReceipts(false);
        return;
      }
      const response = await axios.get(
        `${apiBaseUrl}/main/checkAccountInUse/${dbCode}/${accId || 0}?accountNo=${accNo || ""}&auto=${autoId || ""}`
      );
      if (response.data && (response.data.hasReceipts || response.data.hasRedeem || response.data.inUse)) {
        setHasReceipts(true);
      } else {
        setHasReceipts(false);
      }
    } catch (error) {
      console.error("Error checking account receipts:", error);
      setHasReceipts(false);
    }
  };

  // Refs for all input fields
  const accountNameInputRef = useRef(null);
  const agentNameInputRef = useRef(null);
  const saveButtonRef = useRef(null);
  const editButtonRef = useRef(null);
  const schemeNameInputRef = useRef(null);
  const nomineeNameInputRef = useRef(null);
  const relationInputRef = useRef(null);
  const proofTypeInputRef = useRef(null);
  const idNumberInputRef = useRef(null);
  const nomineeAddress1InputRef = useRef(null);
  const nomineeAddress2InputRef = useRef(null);
  const proofFrontInputRef = useRef(null);
  const proofBackInputRef = useRef(null);
  const entryNoInputRef = useRef(null);
  const durationInputRef = useRef(null);
  const accountNoInputRef = useRef(null);
  const address1InputRef = useRef(null);
  const address2InputRef = useRef(null);
  const cityInputRef = useRef(null);
  const pincodeInputRef = useRef(null);
  const contactPersonInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const telNoInputRef = useRef(null);
  const mobNoInputRef = useRef(null);
  const narrationInputRef = useRef(null);
  const amountInputRef = useRef(null);
  const rateInputRef = useRef(null);
  const gmInputRef = useRef(null);
  const dateInputRef = useRef(null);
  const intperRef = useRef(null);

  const proofFrontBrowseRef = useRef(null);
  const proofBackBrowseRef = useRef(null);

  const addressTabRef = useRef(null);
  const nomineeTabRef = useRef(null);

  const updateNavRefs = (refs) => {
    if (refs.saveButtonRef) {
      saveButtonRef.current = refs.saveButtonRef.current;
    }
    if (refs.editButtonRef) {
      editButtonRef.current = refs.editButtonRef.current;
    }
  };

  // Define the focus order for each tab
  const [focusOrder] = useState({
    address: [
      'entryNo',
      'date',
      'schemeName',
      'accountName',
      'agent',
      'duration',
      'intperc',
      'accountNo',
      'address1',
      'address2',
      'city',
      'pincode',
      'contactPerson',
      'email',
      'telNo',
      'mobNo',
      'tab-nominee'
    ],
    nominee: [
      'tab-address',
      'nomineeName',
      'relation',
      'proofType',
      'idNumber',
      'nomineeAddress1',
      'nomineeAddress2',
      'proofFrontBrowse',
      'proofBackBrowse',
      'narration',
      saveDisabled ? 'editButton' : 'saveButton'
    ]
  });

  // Map of element IDs to their refs
  const elementRefs = {
    'entryNo': entryNoInputRef,
    'date': dateInputRef,
    'schemeName': schemeNameInputRef,
    'accountName': accountNameInputRef,
    'agent': agentNameInputRef,
    'duration': durationInputRef,
    'intperc': intperRef,
    'accountNo': accountNoInputRef,
    'address1': address1InputRef,
    'address2': address2InputRef,
    'city': cityInputRef,
    'pincode': pincodeInputRef,
    'contactPerson': contactPersonInputRef,
    'email': emailInputRef,
    'telNo': telNoInputRef,
    'mobNo': mobNoInputRef,

    'nomineeName': nomineeNameInputRef,
    'relation': relationInputRef,
    'proofType': proofTypeInputRef,
    'idNumber': idNumberInputRef,
    'nomineeAddress1': nomineeAddress1InputRef,
    'nomineeAddress2': nomineeAddress2InputRef,
    'proofFrontBrowse': proofFrontBrowseRef,
    'proofBackBrowse': proofBackBrowseRef,
    'narration': narrationInputRef,

    'saveButton': saveButtonRef,
    'editButton': editButtonRef,

    'tab-address': addressTabRef,
    'tab-nominee': nomineeTabRef,
  };

  const handleKeyDown = (event, elementId) => {
    if (event.key === "Enter" || event.key === "Return") {
      event.preventDefault();

      const currentOrder = focusOrder[activeTab];
      const currentIndex = currentOrder.indexOf(elementId);

      if (currentIndex !== -1) {
        let nextIndex = currentIndex + 1;

        while (nextIndex < currentOrder.length) {
          const nextElementId = currentOrder[nextIndex];

          // Handle tab switching
          if (nextElementId === 'tab-nominee') {
            setActiveTab('nominee');
            setTimeout(() => {
              const nomineeFirstElement = focusOrder.nominee[0];
              if (nomineeFirstElement === 'tab-address') {
                const secondElement = focusOrder.nominee[1];
                const secondRef = elementRefs[secondElement];
                if (secondRef && secondRef.current) {
                  secondRef.current.focus();
                }
              } else {
                const firstRef = elementRefs[nomineeFirstElement];
                if (firstRef && firstRef.current) {
                  firstRef.current.focus();
                }
              }
            }, 50);
            return;
          }

          if (nextElementId === 'tab-address') {
            setActiveTab('address');
            setTimeout(() => {
              const addressFirstElement = focusOrder.address[0];
              const firstRef = elementRefs[addressFirstElement];
              if (firstRef && firstRef.current) {
                firstRef.current.focus();
              }
            }, 50);
            return;
          }

          // Handle disabled save button - skip to edit button
          if (nextElementId === 'saveButton' && saveDisabled && elementRefs['editButton']) {
            nextIndex = currentOrder.indexOf('editButton');
            if (nextIndex > currentIndex) {
              const editRef = elementRefs['editButton'];
              if (editRef && editRef.current) {
                editRef.current.focus();
                return;
              }
            }
          }

          // Get the next element ref
          const nextElementRef = elementRefs[nextElementId];

          if (nextElementRef) {
            // Special handling for ComboBox components
            if (nextElementId === 'schemeName' || nextElementId === 'accountName' ||
                nextElementId === 'agent' || nextElementId === 'proofType') {
              setTimeout(() => {
                if (nextElementRef.current && nextElementRef.current.focusInput) {
                  nextElementRef.current.focusInput();
                } else if (nextElementRef.current) {
                  nextElementRef.current.focus();
                }
              }, 10);
              return;
            }

            // Special handling for file browse buttons
            if (nextElementId === 'proofFrontBrowse' || nextElementId === 'proofBackBrowse') {
              if (nextElementRef.current) {
                nextElementRef.current.focus();
              }
              return;
            }

            // Regular input fields
            if (nextElementRef.current) {
              nextElementRef.current.focus();
              return;
            }
          }

          nextIndex++;
        }

        // If we reach the end of the current tab's order
        if (nextIndex >= currentOrder.length) {
          if (activeTab === 'address') {
            setActiveTab('nominee');
            setTimeout(() => {
              const firstNomineeElement = focusOrder.nominee[0];
              if (firstNomineeElement === 'tab-address') {
                const secondElement = focusOrder.nominee[1];
                const secondRef = elementRefs[secondElement];
                if (secondRef && secondRef.current) {
                  secondRef.current.focus();
                }
              } else {
                const firstRef = elementRefs[firstNomineeElement];
                if (firstRef && firstRef.current) {
                  firstRef.current.focus();
                }
              }
            }, 50);
          }
          else if (activeTab === 'nominee') {
            if (!saveDisabled && saveButtonRef.current) {
              saveButtonRef.current.focus();
            } else if (editButtonRef.current) {
              editButtonRef.current.focus();
            } else {
              const firstElementId = focusOrder.nominee[0];
              const firstRef = elementRefs[firstElementId];
              if (firstRef && firstRef.current) {
                firstRef.current.focus();
              }
            }
          }
        }
      }
    }
  };

  const { minDate, maxDate, isDateInFy } = useActiveFyLimits();

  const getCurrentDate = () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    const formattedToday = `${year}-${month}-${day}`;
    if (minDate && formattedToday < minDate) return minDate;
    if (maxDate && formattedToday > maxDate) return maxDate;
    return formattedToday;
  };

  const today = getCurrentDate();

  const [schemeRegData, setschemeRegData] = useState({
    entryNo: "",
    date: today,
    schemeName: { value: "", label: "" },
    type: "",
    accountName: { value: "", label: "" },
    agent: { value: "", label: "" },
    duration: "",
    accountNo: "",
    add1: "",
    add2: "",
    city: "",
    pincode: "",
    contactPerson: "",
    mobile: "",
    email: "",
    tele: "",
    narration: "",
    amount: 0,
    rate: "",
    gm: 0.0,
    intperc: "",
    nomineeName: "",
    relation: "",
    proofType: { value: "", label: "" },
    idNumber: "",
    nomineeAdd1: "",
    nomineeAdd2: "",
    proofFrontFile: null,
    proofBackFile: null,
    proofFrontFileName: "",
    proofBackFileName: "",
    proofFrontBase64: "",
    proofBackBase64: "",
    isActive: true,
    Location: activeLocation ? activeLocation.id : "",
    auto: "",
  });

  useEffect(() => {
    if (initialAccountName || initialScheme) {
      setschemeRegData((prevData) => ({
        ...prevData,
        ...(initialAccountName && { accountName: { value: "", label: initialAccountName } }),
        ...(initialScheme && { schemeName: { value: initialScheme.value, label: initialScheme.label } })
      }));
    }
  }, [initialAccountName, initialScheme]);

  const [sch_entryno, setSchEntryNo] = useState("");
  const [customernames, setCustomersNames] = useState([]);
  const [AcNames, setANames] = useState([]);
  const [employeeNames, setEmployeeNames] = useState([]);
  const [regcustomers, setregcustomers] = useState([]);
  const [firstEno, setFirstEno] = useState("");
  const [lastEno, setLastEno] = useState("");
  const [countExist, setcountExist] = useState("");
  const [gmvalue, setgmvalue] = useState("");
  const [schemeNames, setschemeNames] = useState([]);

  const handleFileChange = async (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File size should be less than 5MB");
        return;
      }

      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        alert("Only JPG, PNG, and PDF files are allowed");
        return;
      }

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (fileType === 'front') {
            setProofFrontPreview(e.target.result);
          } else {
            setProofBackPreview(e.target.result);
          }
        };
        reader.readAsDataURL(file);
      } else {
        if (fileType === 'front') {
          setProofFrontPreview(null);
        } else {
          setProofBackPreview(null);
        }
      }

      const base64 = await convertToBase64(file);

      if (fileType === 'front') {
        setschemeRegData((prevData) => ({
          ...prevData,
          proofFrontFile: file,
          proofFrontFileName: file.name,
          proofFrontBase64: base64,
        }));
      } else {
        setschemeRegData((prevData) => ({
          ...prevData,
          proofBackFile: file,
          proofBackFileName: file.name,
          proofBackBase64: base64,
        }));
      }
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleRemoveFile = (fileType) => {
    if (fileType === 'front') {
      setschemeRegData((prevData) => ({
        ...prevData,
        proofFrontFile: null,
        proofFrontFileName: "",
        proofFrontBase64: "",
      }));
      setProofFrontPreview(null);
      if (proofFrontInputRef.current) {
        proofFrontInputRef.current.value = "";
      }
    } else {
      setschemeRegData((prevData) => ({
        ...prevData,
        proofBackFile: null,
        proofBackFileName: "",
        proofBackBase64: "",
      }));
      setProofBackPreview(null);
      if (proofBackInputRef.current) {
        proofBackInputRef.current.value = "";
      }
    }
  };

  const showFilePreview = (fileType) => {
    let fileData = null;
    let fileName = "";

    if (fileType === 'front') {
      fileData = schemeRegData.proofFrontBase64;
      fileName = schemeRegData.proofFrontFileName;
    } else {
      fileData = schemeRegData.proofBackBase64;
      fileName = schemeRegData.proofBackFileName;
    }

    if (!fileData) {
      alert("No file to preview");
      return;
    }

    const previewWindow = window.open('', '_blank');
    previewWindow.document.write(`
      <html>
        <head>
          <title>Preview - ${fileName}</title>
          <style>
            body { margin: 0; padding: 20px; display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f5; }
            img { max-width: 90vw; max-height: 80vh; box-shadow: 0 4px 8px rgba(0,0,0,0.2); margin-bottom: 20px; }
            .pdf-message { font-family: Arial, sans-serif; padding: 20px; background: white; border-radius: 8px; box-shadow: 0 4px 8px rgba(0,0,0,0.2); text-align: center; }
            .button-container { margin-top: 20px; display: flex; gap: 10px; }
            .download-btn { padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer; text-decoration: none; }
            .download-btn:hover { background: #0056b3; }
            .close-btn { padding: 10px 20px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer; }
            .close-btn:hover { background: #545b62; }
          </style>
        </head>
        <body>
          ${fileName.endsWith('.pdf')
        ? `<div class="pdf-message">
                 <h3>PDF File Preview</h3>
                 <p>File: ${fileName}</p>
                 <p>PDF files cannot be previewed inline.</p>
                 <div class="button-container">
                   <a class="download-btn" href="data:application/pdf;base64,${fileData}" download="${fileName}">Download PDF</a>
                   <button class="close-btn" onclick="window.close()">Close</button>
                 </div>
               </div>`
        : `<img src="data:image/jpeg;base64,${fileData}" alt="${fileName}" />
               <div class="button-container">
                 <a class="download-btn" href="data:image/jpeg;base64,${fileData}" download="${fileName}">Download Image</a>
                 <button class="close-btn" onclick="window.close()">Close</button>
               </div>`}
        </body>
      </html>
    `);
    previewWindow.document.close();
  };

  const downloadFile = (fileType) => {
    let fileData = null;
    let fileName = "";
    let mimeType = "";

    if (fileType === 'front') {
      fileData = schemeRegData.proofFrontBase64;
      fileName = schemeRegData.proofFrontFileName;
      mimeType = fileName.endsWith('.pdf') ? 'application/pdf' : 'image/jpeg';
    } else {
      fileData = schemeRegData.proofBackBase64;
      fileName = schemeRegData.proofBackFileName;
      mimeType = fileName.endsWith('.pdf') ? 'application/pdf' : 'image/jpeg';
    }

    if (!fileData || !fileName) {
      alert("No file available to download");
      return;
    }

    const link = document.createElement('a');
    link.href = `data:${mimeType};base64,${fileData}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const calculateGm = () => {
    const amount = parseFloat(schemeRegData.amount);
    const rate = parseFloat(schemeRegData.rate);

    if (!isNaN(amount) && !isNaN(rate) && rate !== 0) {
      const gmValue = amount / rate;
      setgmvalue(gmValue);
      setschemeRegData((prevData) => ({
        ...prevData,
        gm: gmValue.toFixed(3),
      }));
    } else {
      setschemeRegData((prevData) => ({
        ...prevData,
        gm: "",
      }));
    }
  };

  // ─── Component-level data fetch helpers ─────────────────────────────────────
  // Defined at component scope so socket callbacks always use the latest
  // apiBaseUrl/dbCode values — avoiding stale closure bugs.

  const fetchAccountNames = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/main/accountNames/${dbCode}`);
      const schemeDet = response.data;
      const AccNames = schemeDet.map((item) => ({
        value: item.Ledcode,
        label: item.LedName
      }));
      const cnames = schemeDet.map((item) => item.Ledcode);
      setANames(AccNames);
      setCustomersNames(cnames);
      return AccNames;
    } catch (error) {
      console.error("Error fetching account names:", error.message);
      return [];
    }
  };

  const fetchSchCurrentRate = async (targetDate) => {
    try {
      const dateToUse = targetDate || schemeRegData.date;
      const response = await axios.get(`${apiBaseUrl}/main/getGoldRate/${dbCode}?date=${dateToUse || ""}`);
      const currentrate = response.data[0]?.currentRate;
      setschemeRegData((prevData) => ({ ...prevData, rate: currentrate || 0 }));
    } catch (error) {
      console.error("Error fetching current rate:", error.message);
    }
  };

  const fetchupdschregisteredcustomers = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/main/registeredcustomers/${dbCode}`);
      setregcustomers(response.data);
    } catch (error) {
      console.error("Error fetching sch reg names:", error.message);
    }
  };

  const fetchSchEntryno = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/main/Sch_entryno/${dbCode}`);
      const firstItem = response.data?.[0];
      const nextEntry = firstItem?.entryNo ?? firstItem?.[""] ?? 1;
      const nextAcc = firstItem?.nextAccNo ?? nextEntry;
      setschemeRegData((prevData) => ({
        ...prevData,
        entryNo: nextEntry,
        accountNo: String(nextAcc).padStart(3, "0"),
      }));
      setSchEntryNo(nextEntry);
    } catch (error) {
      console.error("Error fetching entry no:", error.message);
    }
  };

  const fetchSchemes = async () => {
    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/getSchemes/${dbCode}`
      );

      if (response.data.success) {
        // Direct value-label mapping
        const formattedSchemes = response.data.data.map((item) => ({
          value: item.SchemeId,
          label: item.SchemeName,
          duration: item.Duration,
          type: item.SchemeType || "Monthly",
        }));

        setschemeNames(formattedSchemes);
      }
    } catch (error) {
      console.error("Error fetching schemes:", error);
    }
  };

  const handleSchemeSelect = async (opt) => {
    if (!opt) {
      setschemeRegData((prevData) => ({
        ...prevData,
        schemeName: { value: "", label: "" },
        duration: "",
        type: "",
      }));
      if (!dataFound) {
        fetchSchEntryno(null);
      }
      return;
    }

    setschemeRegData((prevData) => ({
      ...prevData,
      schemeName: opt,
      duration: opt.duration !== undefined && opt.duration !== null ? opt.duration : prevData.duration,
      type: opt.type || prevData.type || "Monthly",
    }));

    // Fetch next account number for this specific scheme
    if (!dataFound && opt.value) {
      try {
        const entryRes = await axios.get(`${apiBaseUrl}/main/Sch_entryno/${dbCode}?schemeId=${opt.value}`);
        const firstItem = entryRes.data?.[0];
        if (firstItem) {
          const formattedAcc = firstItem.formattedAccNo || String(firstItem.nextAccNo || 1).padStart(3, "0");
          setschemeRegData((prevData) => ({
            ...prevData,
            accountNo: formattedAcc,
          }));
        }
      } catch (e) {
        console.error("Error fetching scheme account number:", e);
      }
    }

    // Fetch details directly from SchemeManagement table to ensure latest values
    try {
      let schemeDetail = null;
      if (opt.value) {
        const res = await axios.get(`${apiBaseUrl}/main/findSchemeById/${dbCode}/${opt.value}`);
        if (res.data?.success && res.data.data) {
          schemeDetail = res.data.data;
        }
      }
      if (!schemeDetail && opt.label) {
        const res = await axios.get(`${apiBaseUrl}/main/findSchemeByName/${dbCode}/${encodeURIComponent(opt.label)}`);
        if (res.data?.success && res.data.data) {
          schemeDetail = res.data.data;
        }
      }
      if (schemeDetail) {
        setschemeRegData((prevData) => ({
          ...prevData,
          duration: schemeDetail.Duration !== undefined && schemeDetail.Duration !== null ? schemeDetail.Duration : prevData.duration,
          type: schemeDetail.SchemeType || schemeDetail.type || prevData.type || "Monthly",
        }));
      }
    } catch (err) {
      console.error("Error fetching scheme details:", err);
    }
  };

  useEffect(() => {
    calculateGm();
  }, [schemeRegData.amount, schemeRegData.rate]);

  useEffect(() => {
    fetchSchEntryno();

    const fetchEmployeeNames = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/main/SchReg_empname/${dbCode}`);
        const empNames = response.data.map((item) => ({
          value: item.Auto,
          label: item.name
        }));
        setEmployeeNames(empNames);
      } catch (error) {
        console.error("Error fetching employee names:", error.message);
      }
    };

    // Use component-level fetchAccountNames (not a local copy) to avoid stale closure
    fetchAccountNames();
    fetchEmployeeNames();
    fetchupdschregisteredcustomers();
    fetchSchCurrentRate(schemeRegData.date);
    fetchSchemes();

    const socket = io(apiBaseUrl);
    socket.on("entrynoUpdated", () => {
      fetchSchEntryno();
    });
    socket.on("EmployeeUpdated", () => {
      fetchEmployeeNames();
    });
    socket.on("LedgerUpdated", async () => {
      const updatedAccNames = await fetchAccountNames();

      setTimeout(() => {
        if (sessionStorage.getItem("schemeRegAccountNameFocus") === "true") {
          const typedName = sessionStorage.getItem("schemeRegTypedAccountName");
          if (typedName) {
            const matched = updatedAccNames.find(
              (item) => item.label.toLowerCase() === typedName.toLowerCase()
            );
            if (matched) {
              setschemeRegData((prevData) => ({
                ...prevData,
                accountName: matched,
              }));
            }
            sessionStorage.removeItem("schemeRegTypedAccountName");
          }

          if (accountNameInputRef.current) {
            if (accountNameInputRef.current.focusInput) {
              accountNameInputRef.current.focusInput();
            } else {
              accountNameInputRef.current.focus();
            }
          }
          sessionStorage.removeItem("schemeRegAccountNameFocus");
        }
      }, 500);
    });

    socket.on("SchemeUpdated", () => {
      fetchSchemes();

      setschemeRegData((prevData) => {
        if (prevData.schemeName?.value) {
          axios
            .get(`${apiBaseUrl}/main/findSchemeById/${dbCode}/${prevData.schemeName.value}`)
            .then((res) => {
              if (res.data?.success && res.data.data) {
                const schemeDetail = res.data.data;
                setschemeRegData((innerPrev) => ({
                  ...innerPrev,
                  duration: schemeDetail.Duration !== undefined && schemeDetail.Duration !== null ? schemeDetail.Duration : innerPrev.duration,
                  type: schemeDetail.SchemeType || schemeDetail.type || innerPrev.type || "Monthly",
                }));
              }
            })
            .catch(console.error);
        }
        return prevData;
      });

      setTimeout(() => {
        if (sessionStorage.getItem("schemeRegSchemeNameFocus") === "true") {
          if (schemeNameInputRef.current) {
            if (schemeNameInputRef.current.focusInput) {
              schemeNameInputRef.current.focusInput();
            } else {
              schemeNameInputRef.current.focus();
            }
          }
          sessionStorage.removeItem("schemeRegSchemeNameFocus");
        }
      }, 500);
    });

    return () => socket.disconnect();
  }, [apiBaseUrl, dbCode]);

  useEffect(() => {
    setTimeout(() => {
      if (entryNoInputRef.current) {
        entryNoInputRef.current.focus();
      }
    }, 100);
  }, []);

  useEffect(() => {
    if (schemeRegData.date && dbCode) {
      fetchSchCurrentRate(schemeRegData.date);
    }
  }, [schemeRegData.date, dbCode, apiBaseUrl]);

  useEffect(() => {
    if (activeTab === 'address') {
      setTimeout(() => {
        if (entryNoInputRef.current) {
          entryNoInputRef.current.focus();
        }
      }, 50);
    } else if (activeTab === 'nominee') {
      setTimeout(() => {
        if (nomineeNameInputRef.current) {
          nomineeNameInputRef.current.focus();
        }
      }, 50);
    }
  }, [activeTab]);

  const fetchcustomerdetails = async (accountId) => {
    if (!accountId) return;

    try {
      const response = await axios.get(`${apiBaseUrl}/main/scheRegcustomerDetails/${accountId}/${dbCode}`);
      if (response.status === 200) {
        const foundData = response.data[0];
        const matchAcc = AcNames.find((item) => item.value === foundData.Ledcode);
        setschemeRegData({
          ...schemeRegData,
          accountName: matchAcc ? { value: matchAcc.value, label: matchAcc.label } : { value: foundData.Ledcode, label: "" },
          add1: foundData.add1,
          add2: foundData.add2,
          pincode: foundData.PinNo,
          mobile: foundData.Mobile,
          email: foundData.Email,
          tele: foundData.Telno,
        });
      }
    } catch (error) {
      console.error("Error fetching ledger datas:", error.message);
    }
  };

  const handleClear = () => {
    setschemeRegData({
      entryNo: sch_entryno,
      date: getCurrentDate(),
      schemeName: { value: "", label: "" },
      type: "",
      accountName: { value: "", label: "" },
      agent: { value: "", label: "" },
      duration: "",
      accountNo: "",
      add1: "",
      add2: "",
      city: "",
      pincode: "",
      contactPerson: "",
      mobile: "",
      email: "",
      tele: "",
      narration: "",
      amount: 0,
      intperc: "",
      nomineeName: "",
      relation: "",
      proofType: { value: "", label: "" },
      idNumber: "",
      nomineeAdd1: "",
      nomineeAdd2: "",
      proofFrontFile: null,
      proofBackFile: null,
      proofFrontFileName: "",
      proofBackFileName: "",
      proofFrontBase64: "",
      proofBackBase64: "",
      isActive: true,
      Location: activeLocation ? activeLocation.id : "",
      auto: "",
    });

    setProofFrontPreview(null);
    setProofBackPreview(null);
    setDataFound(false);
    setEditDisabled(true);
    setDeleteDisabled(true);
    setSaveDisabled(false);
    setHasReceipts(false);
    setOriginalAccountInfo({ accountName: null, schemeName: null, accountNo: "" });
    setActiveTab('address');

    setTimeout(() => {
      if (entryNoInputRef.current) {
        entryNoInputRef.current.focus();
      }
    }, 100);
  };

  const handleClearFind = () => {
    setschemeRegData({
      accountName: { value: "", label: "" },
      agent: { value: "", label: "" },
      duration: "",
      type: "",
      accountNo: "",
      add1: "",
      add2: "",
      city: "",
      pincode: "",
      contactPerson: "",
      mobile: "",
      email: "",
      tele: "",
      narration: "",
      amount: 0,
      intperc: "",
      nomineeName: "",
      relation: "",
      proofType: { value: "", label: "" },
      idNumber: "",
      nomineeAdd1: "",
      nomineeAdd2: "",
      proofFrontFileName: "",
      proofBackFileName: "",
      proofFrontBase64: "",
      proofBackBase64: "",
      isActive: true,
      Location: activeLocation ? activeLocation.id : "",
      auto: "",
    });

    setProofFrontPreview(null);
    setProofBackPreview(null);
    setHasReceipts(false);
    setOriginalAccountInfo({ accountName: null, schemeName: null, accountNo: "" });
    setActiveTab('address');

    setTimeout(() => {
      if (entryNoInputRef.current) {
        entryNoInputRef.current.focus();
      }
    }, 100);

    setEditDisabled(true);
    setDeleteDisabled(true);
    setSaveDisabled(false);
  };

  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const checkDuplicateInSameScheme = (schemeId, accountId, currentEntryNo = null) => {
    if (!schemeId || !accountId) return false;

    return regcustomers.some(customer =>
      customer.accname === accountId &&
      customer.schemeId === schemeId &&
      (currentEntryNo === null || customer.entryno !== currentEntryNo)
    );
  };

  const handleSubmit = async () => {
    setSaveDisabled(true);
    try {
      if (schemeRegData.mobile && schemeRegData.mobile.length !== 10 && schemeRegData.mobile.length !== 0) {
        alert("Mobile number must be 10 digits.");
        setSaveDisabled(false);
        return;
      }

      const selectedSchemeId = schemeRegData.schemeName?.value;
      if (!selectedSchemeId) {
        alert("Please select Scheme Name.");
        setSaveDisabled(false);
        return;
      }

      const selectedAccountId = schemeRegData.accountName?.value;
      if (!selectedAccountId) {
        alert("Please enter account name.");
        setSaveDisabled(false);
        return;
      }

      if (schemeRegData.accountNo === "") {
        alert("Please enter account no.");
        setSaveDisabled(false);
        return;
      }

      const isAccNoDuplicate = regcustomers.some(
        (customer) => String(customer.accno || "").trim().toLowerCase() === String(schemeRegData.accountNo).trim().toLowerCase()
      );
      if (isAccNoDuplicate) {
        alert(`Account Number "${schemeRegData.accountNo}" is already in use. Duplicate account numbers are not allowed.`);
        setSaveDisabled(false);
        return;
      }

      if (schemeRegData.email && !validateEmail(schemeRegData.email)) {
        alert("Invalid email address.");
        setSaveDisabled(false);
        return;
      }

      if (!customernames.includes(selectedAccountId)) {
        alert("Customer is not registered.");
        setSaveDisabled(false);
        return;
      }

      const isDuplicateInSameScheme = checkDuplicateInSameScheme(selectedSchemeId, selectedAccountId);

      if (isDuplicateInSameScheme) {
        alert(`This account name is already registered for the selected scheme. Duplicate account names are not allowed within the same scheme.`);
        setSaveDisabled(false);
        return;
      }

      const updatedSchemeRegData = {
        ...schemeRegData,
        agent: schemeRegData.agent?.value || 0,
        accountName: schemeRegData.accountName?.value,
        schemeName: schemeRegData.schemeName?.value,
        proofFrontBase64: schemeRegData.proofFrontBase64,
        proofBackBase64: schemeRegData.proofBackBase64,
        proofType: schemeRegData.proofType?.label || schemeRegData.proofType?.value || "",
        isActive: schemeRegData.isActive ? 1 : 0,
      };

      const response = await axios.post(`${apiBaseUrl}/main/insertScheme/${dbCode}`, updatedSchemeRegData);

      if (response.data.success) {
        alert("Account registered successfully!");
        handleClear();
        fetchSchEntryno();
        if (initialAccountName) {
          onClose();
        }
      } else {
        if (response.data.message === "Duplicate account number") {
          alert("Duplicate account number.");
        } else {
          alert("Error submitting data. Please try again.");
        }
        setSaveDisabled(false);
      }
      await fetchupdschregisteredcustomers();
    } catch (error) {
      setSaveDisabled(false);
      console.error("Error:", error.message);
      alert("An error occurred. Please try again.");
    }
  };

  const handleFind = async () => {
    handleClearFind();
    try {
      const selectedAccountId = schemeRegData.accountName?.value;

      if (regcustomers.some(customer => customer.accname === selectedAccountId)) {
        const response = await axios.get(`${apiBaseUrl}/main/findbyAccname/${selectedAccountId}/${dbCode}`);

        if (response.status === 200) {
          const foundData = response.data[0];
          const formattedDate = new Date(foundData.Date).toISOString().split("T")[0];

          if (foundData.proofFrontBase64) {
            setProofFrontPreview(`data:image/jpeg;base64,${foundData.proofFrontBase64}`);
          }
          if (foundData.proofBackBase64) {
            setProofBackPreview(`data:image/jpeg;base64,${foundData.proofBackBase64}`);
          }

          const matchScheme = schemeNames.find((item) => item.value === foundData.schemeId);
          const matchAcc = AcNames.find((item) => item.value === foundData.accname);
          const matchAgent = employeeNames.find((item) => item.value === foundData.agent);

          setschemeRegData({
            ...schemeRegData,
            entryNo: foundData.entryno,
            date: formattedDate,
            schemeName: matchScheme ? { value: matchScheme.value, label: matchScheme.label } : { value: foundData.schemeId, label: "" },
            accountName: matchAcc ? { value: matchAcc.value, label: matchAcc.label } : { value: foundData.accname, label: "" },
            agent: matchAgent ? { value: matchAgent.value, label: matchAgent.label } : { value: foundData.agent, label: "" },
            duration: foundData.duration,
            type: foundData.type || matchScheme?.type || "",
            accountNo: foundData.accno,
            add1: foundData.Address1,
            add2: foundData.Address2,
            city: foundData.City,
            pincode: foundData.pin,
            contactPerson: foundData.contactperson,
            mobile: foundData.mobileNo,
            email: foundData.email,
            tele: foundData.phoneNo,
            narration: foundData.NARRATION,
            amount: foundData.Amount,
            rate: foundData.rate,
            gm: foundData.grm,
            intperc: foundData.intperc,
            nomineeName: foundData.nomineeName || "",
            relation: foundData.relation || "",
            proofType: { value: foundData.proofType, label: foundData.proofType || "" },
            idNumber: foundData.idNumber || "",
            nomineeAdd1: foundData.nomineeAdd1 || "",
            nomineeAdd2: foundData.nomineeAdd2 || "",
            proofFrontFileName: foundData.proofFrontFileName || "",
            proofBackFileName: foundData.proofBackFileName || "",
            proofFrontBase64: foundData.proofFrontBase64 || "",
            proofBackBase64: foundData.proofBackBase64 || "",
            isActive: foundData.isActive === 1 || foundData.isActive === true,
          auto: foundData.auto || "",
          });

          setDataFound(true);
          setEditDisabled(false);
          setDeleteDisabled(false);
          setSaveDisabled(true);
          checkAccountReceipts(foundData.accname, foundData.accno, foundData.auto, foundData.schemeId);
        } else {
          console.error("Error retrieving data:", response.data);
          setDataFound(false);
        }
      } else {
        if (!selectedAccountId) {
          alert("Select an Account Name");
        } else {
          alert("Account Not Registered");
        }
      }
    } catch (error) {
      console.error("Error:", error.message);
      setDataFound(false);
    }
  };

  const handleSearchByAccountNo = async () => {
    handleClearFind();
    try {
      const response = await axios.get(`${apiBaseUrl}/main/findbyAccno/${dbCode}`, {
        params: { accNo: schemeRegData.accountNo }
      });
      if (response.status === 200) {
        const foundData = response.data[0];
        const formattedDate = new Date(foundData.Date).toISOString().split("T")[0];

        if (foundData.proofFrontBase64) {
          setProofFrontPreview(`data:image/jpeg;base64,${foundData.proofFrontBase64}`);
        }
        if (foundData.proofBackBase64) {
          setProofBackPreview(`data:image/jpeg;base64,${foundData.proofBackBase64}`);
        }

        const matchScheme = schemeNames.find((item) => item.value === foundData.schemeId);
        const matchAcc = AcNames.find((item) => item.value === foundData.accname);
        const matchAgent = employeeNames.find((item) => item.value === foundData.agent);

        setschemeRegData({
          ...schemeRegData,
          entryNo: foundData.entryno,
          date: formattedDate,
          schemeName: matchScheme ? { value: matchScheme.value, label: matchScheme.label } : { value: foundData.schemeId, label: "" },
          accountName: matchAcc ? { value: matchAcc.value, label: matchAcc.label } : { value: foundData.accname, label: "" },
          agent: matchAgent ? { value: matchAgent.value, label: matchAgent.label } : { value: foundData.agent, label: "" },
          duration: foundData.duration,
          type: foundData.type || matchScheme?.type || "",
          accountNo: foundData.accno,
          add1: foundData.Address1,
          add2: foundData.Address2,
          city: foundData.City,
          pincode: foundData.pin,
          contactPerson: foundData.contactperson,
          mobile: foundData.mobileNo,
          email: foundData.email,
          tele: foundData.phoneNo,
          narration: foundData.NARRATION,
          amount: foundData.Amount,
          rate: foundData.rate,
          gm: foundData.grm,
          intperc: foundData.intperc,
          nomineeName: foundData.nomineeName || "",
          relation: foundData.relation || "",
          proofType: { value: foundData.proofType, label: foundData.proofType || "" },
          idNumber: foundData.idNumber || "",
          nomineeAdd1: foundData.nomineeAdd1 || "",
          nomineeAdd2: foundData.nomineeAdd2 || "",
          proofFrontFileName: foundData.proofFrontFileName || "",
          proofBackFileName: foundData.proofBackFileName || "",
          proofFrontBase64: foundData.proofFrontBase64 || "",
          proofBackBase64: foundData.proofBackBase64 || "",
          isActive: foundData.isActive === 1 || foundData.isActive === true,
          auto: foundData.auto || "",
        });
        setDataFound(true);
        setEditDisabled(false);
        setDeleteDisabled(false);
        setSaveDisabled(true);
        checkAccountReceipts(foundData.accname, foundData.accno, foundData.auto, foundData.schemeId);
      }
    } catch (error) {
      console.error("Error:", error.message);
      setDataFound(false);
    }
  };

  const handleFindFirst = async () => {
    handleClearFind();
    try {
      const response = await axios.get(`${apiBaseUrl}/main/SchRegfindfirst/${schemeRegData.entryNo}/${dbCode}`);
      if (response.status === 200) {
        const foundData = response.data[0];
        const formattedDate = new Date(foundData.Date).toISOString().split("T")[0];

        if (foundData.proofFrontBase64) {
          setProofFrontPreview(`data:image/jpeg;base64,${foundData.proofFrontBase64}`);
        }
        if (foundData.proofBackBase64) {
          setProofBackPreview(`data:image/jpeg;base64,${foundData.proofBackBase64}`);
        }

        const matchScheme = schemeNames.find((item) => item.value === foundData.schemeId);
        const matchAcc = AcNames.find((item) => item.value === foundData.accname);
        const matchAgent = employeeNames.find((item) => item.value === foundData.agent);

        setschemeRegData({
          ...schemeRegData,
          entryNo: foundData.entryno,
          date: formattedDate,
          schemeName: matchScheme ? { value: matchScheme.value, label: matchScheme.label } : { value: foundData.schemeId, label: "" },
          accountName: matchAcc ? { value: matchAcc.value, label: matchAcc.label } : { value: foundData.accname, label: "" },
          agent: matchAgent ? { value: matchAgent.value, label: matchAgent.label } : { value: foundData.agent, label: "" },
          duration: foundData.duration,
          type: foundData.type || matchScheme?.type || "",
          accountNo: foundData.accno,
          add1: foundData.Address1,
          add2: foundData.Address2,
          city: foundData.City,
          pincode: foundData.pin,
          contactPerson: foundData.contactperson,
          mobile: foundData.mobileNo,
          email: foundData.email,
          tele: foundData.phoneNo,
          narration: foundData.NARRATION,
          amount: foundData.Amount,
          rate: foundData.rate,
          gm: foundData.grm,
          intperc: foundData.intperc,
          nomineeName: foundData.nomineeName || "",
          relation: foundData.relation || "",
          proofType: { value: foundData.proofType, label: foundData.proofType || "" },
          idNumber: foundData.idNumber || "",
          nomineeAdd1: foundData.nomineeAdd1 || "",
          nomineeAdd2: foundData.nomineeAdd2 || "",
          proofFrontFileName: foundData.proofFrontFileName || "",
          proofBackFileName: foundData.proofBackFileName || "",
          proofFrontBase64: foundData.proofFrontBase64 || "",
          proofBackBase64: foundData.proofBackBase64 || "",
          isActive: foundData.isActive === 1 || foundData.isActive === true,
          auto: foundData.auto || "",
        });
        setDataFound(true);
        setEditDisabled(false);
        setDeleteDisabled(false);
        setSaveDisabled(true);
        checkAccountReceipts(foundData.accname, foundData.accno, foundData.auto, foundData.schemeId);
      }
    } catch (error) {
      console.error("Error:", error.message);
      setDataFound(false);
    }
  };

  const handleFindLast = async () => {
    handleClearFind();
    try {
      const response = await axios.get(`${apiBaseUrl}/main/SchRegfindlast/${schemeRegData.entryNo}/${dbCode}`);
      if (response.status === 200) {
        const foundData = response.data[0];
        const formattedDate = new Date(foundData.Date).toISOString().split("T")[0];

        if (foundData.proofFrontBase64) {
          setProofFrontPreview(`data:image/jpeg;base64,${foundData.proofFrontBase64}`);
        }
        if (foundData.proofBackBase64) {
          setProofBackPreview(`data:image/jpeg;base64,${foundData.proofBackBase64}`);
        }

        const matchScheme = schemeNames.find((item) => item.value === foundData.schemeId);
        const matchAcc = AcNames.find((item) => item.value === foundData.accname);
        const matchAgent = employeeNames.find((item) => item.value === foundData.agent);

        setschemeRegData({
          ...schemeRegData,
          entryNo: foundData.entryno,
          date: formattedDate,
          schemeName: matchScheme ? { value: matchScheme.value, label: matchScheme.label } : { value: foundData.schemeId, label: "" },
          accountName: matchAcc ? { value: matchAcc.value, label: matchAcc.label } : { value: foundData.accname, label: "" },
          agent: matchAgent ? { value: matchAgent.value, label: matchAgent.label } : { value: foundData.agent, label: "" },
          duration: foundData.duration,
          type: foundData.type || matchScheme?.type || "",
          accountNo: foundData.accno,
          add1: foundData.Address1,
          add2: foundData.Address2,
          city: foundData.City,
          pincode: foundData.pin,
          contactPerson: foundData.contactperson,
          mobile: foundData.mobileNo,
          email: foundData.email,
          tele: foundData.phoneNo,
          narration: foundData.NARRATION,
          amount: foundData.Amount,
          rate: foundData.rate,
          gm: foundData.grm,
          intperc: foundData.intperc,
          nomineeName: foundData.nomineeName || "",
          relation: foundData.relation || "",
          proofType: { value: foundData.proofType, label: foundData.proofType || "" },
          idNumber: foundData.idNumber || "",
          nomineeAdd1: foundData.nomineeAdd1 || "",
          nomineeAdd2: foundData.nomineeAdd2 || "",
          proofFrontFileName: foundData.proofFrontFileName || "",
          proofBackFileName: foundData.proofBackFileName || "",
          proofFrontBase64: foundData.proofFrontBase64 || "",
          proofBackBase64: foundData.proofBackBase64 || "",
          isActive: foundData.isActive === 1 || foundData.isActive === true,
          auto: foundData.auto || "",
        });
        setDataFound(true);
        setEditDisabled(false);
        setDeleteDisabled(false);
        setSaveDisabled(true);
        checkAccountReceipts(foundData.accname, foundData.accno, foundData.auto, foundData.schemeId);
      }
    } catch (error) {
      console.error("Error:", error.message);
      setDataFound(false);
    }
  };

  const fetchfirstandlast = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/main/ScheRegtopandlast/${dbCode}`);
      if (response.status === 200) {
        setFirstEno(response.data[0].firstentryno);
        setLastEno(response.data[0].lastentryno);
      }
    } catch (error) {
      console.error("Error fetching data:", error.message);
    }
  };

  const handleFindNext = async () => {
    fetchfirstandlast();
    if (schemeRegData.entryNo < firstEno) {
      handleClearFind();
    }
    try {
      const response = await axios.get(`${apiBaseUrl}/main/SchRegfindnext/${schemeRegData.entryNo}/${dbCode}`);
      if (response.status === 200) {
        const foundData = response.data[0];
        const formattedDate = new Date(foundData.Date).toISOString().split("T")[0];

        if (foundData.proofFrontBase64) {
          setProofFrontPreview(`data:image/jpeg;base64,${foundData.proofFrontBase64}`);
        }
        if (foundData.proofBackBase64) {
          setProofBackPreview(`data:image/jpeg;base64,${foundData.proofBackBase64}`);
        }

        const matchScheme = schemeNames.find((item) => item.value === foundData.schemeId);
        const matchAcc = AcNames.find((item) => item.value === foundData.accname);
        const matchAgent = employeeNames.find((item) => item.value === foundData.agent);

        setschemeRegData({
          ...schemeRegData,
          entryNo: foundData.entryno,
          date: formattedDate,
          schemeName: matchScheme ? { value: matchScheme.value, label: matchScheme.label } : { value: foundData.schemeId, label: "" },
          accountName: matchAcc ? { value: matchAcc.value, label: matchAcc.label } : { value: foundData.accname, label: "" },
          agent: matchAgent ? { value: matchAgent.value, label: matchAgent.label } : { value: foundData.agent, label: "" },
          duration: foundData.duration,
          type: foundData.type || matchScheme?.type || "",
          accountNo: foundData.accno,
          add1: foundData.Address1,
          add2: foundData.Address2,
          city: foundData.City,
          pincode: foundData.pin,
          contactPerson: foundData.contactperson,
          mobile: foundData.mobileNo,
          email: foundData.email,
          tele: foundData.phoneNo,
          narration: foundData.NARRATION,
          amount: foundData.Amount,
          rate: foundData.rate,
          gm: foundData.grm,
          intperc: foundData.intperc,
          nomineeName: foundData.nomineeName || "",
          relation: foundData.relation || "",
          proofType: { value: foundData.proofType, label: foundData.proofType || "" },
          idNumber: foundData.idNumber || "",
          nomineeAdd1: foundData.nomineeAdd1 || "",
          nomineeAdd2: foundData.nomineeAdd2 || "",
          proofFrontFileName: foundData.proofFrontFileName || "",
          proofBackFileName: foundData.proofBackFileName || "",
          proofFrontBase64: foundData.proofFrontBase64 || "",
          proofBackBase64: foundData.proofBackBase64 || "",
          isActive: foundData.isActive === 1 || foundData.isActive === true,
          auto: foundData.auto || "",
        });
        setDataFound(true);
        setEditDisabled(false);
        setDeleteDisabled(false);
        setSaveDisabled(true);
        checkAccountReceipts(foundData.accname, foundData.accno, foundData.auto, foundData.schemeId);
      }
    } catch (error) {
      console.error("Error:", error.message);
      setDataFound(false);
    }
  };

  const handleFindprev = async () => {
    fetchfirstandlast();
    if (schemeRegData.entryNo > firstEno) {
      handleClearFind();
    }
    try {
      const response = await axios.get(`${apiBaseUrl}/main/SchRegfindprev/${schemeRegData.entryNo}/${dbCode}`);
      if (response.status === 200) {
        const foundData = response.data[0];
        const formattedDate = new Date(foundData.Date).toISOString().split("T")[0];

        if (foundData.proofFrontBase64) {
          setProofFrontPreview(`data:image/jpeg;base64,${foundData.proofFrontBase64}`);
        }
        if (foundData.proofBackBase64) {
          setProofBackPreview(`data:image/jpeg;base64,${foundData.proofBackBase64}`);
        }

        const matchScheme = schemeNames.find((item) => item.value === foundData.schemeId);
        const matchAcc = AcNames.find((item) => item.value === foundData.accname);
        const matchAgent = employeeNames.find((item) => item.value === foundData.agent);

        setschemeRegData({
          ...schemeRegData,
          entryNo: foundData.entryno,
          date: formattedDate,
          schemeName: matchScheme ? { value: matchScheme.value, label: matchScheme.label } : { value: foundData.schemeId, label: "" },
          accountName: matchAcc ? { value: matchAcc.value, label: matchAcc.label } : { value: foundData.accname, label: "" },
          agent: matchAgent ? { value: matchAgent.value, label: matchAgent.label } : { value: foundData.agent, label: "" },
          duration: foundData.duration,
          type: foundData.type || matchScheme?.type || "",
          accountNo: foundData.accno,
          add1: foundData.Address1,
          add2: foundData.Address2,
          city: foundData.City,
          pincode: foundData.pin,
          contactPerson: foundData.contactperson,
          mobile: foundData.mobileNo,
          email: foundData.email,
          tele: foundData.phoneNo,
          narration: foundData.NARRATION,
          amount: foundData.Amount,
          rate: foundData.rate,
          gm: foundData.grm,
          intperc: foundData.intperc,
          nomineeName: foundData.nomineeName || "",
          relation: foundData.relation || "",
          proofType: { value: foundData.proofType, label: foundData.proofType || "" },
          idNumber: foundData.idNumber || "",
          nomineeAdd1: foundData.nomineeAdd1 || "",
          nomineeAdd2: foundData.nomineeAdd2 || "",
          proofFrontFileName: foundData.proofFrontFileName || "",
          proofBackFileName: foundData.proofBackFileName || "",
          proofFrontBase64: foundData.proofFrontBase64 || "",
          proofBackBase64: foundData.proofBackBase64 || "",
          isActive: foundData.isActive === 1 || foundData.isActive === true,
          auto: foundData.auto || "",
        });
        setDataFound(true);
        setEditDisabled(false);
        setDeleteDisabled(false);
        setSaveDisabled(true);
        checkAccountReceipts(foundData.accname, foundData.accno, foundData.auto, foundData.schemeId);
      }
    } catch (error) {
      console.error("Error:", error.message);
      setDataFound(false);
    }
  };

  const handleUpdate = async () => {
    try {
      const mobileNumber = schemeRegData.mobile ? schemeRegData.mobile.toString() : "";
      if (schemeRegData.mobile && mobileNumber.length !== 10 && mobileNumber.length !== 0) {
        alert("Invalid Mobile Number.");
        return;
      }
      if (schemeRegData.email && !validateEmail(schemeRegData.email)) {
        alert("Invalid email address");
        return;
      }

      const selectedSchemeId = schemeRegData.schemeName?.value;
      if (!selectedSchemeId) {
        alert("Please select Scheme Name.");
        return;
      }

      if (dataFound === true) {
        const selectedAccountId = schemeRegData.accountName?.value;

        if (hasReceipts) {
          const curAccName = schemeRegData.accountName?.value;
          const curSchemeId = schemeRegData.schemeName?.value;
          const curAccNo = String(schemeRegData.accountNo || "").trim();

          const isAccNameChanged = originalAccountInfo.accountName !== null && curAccName !== originalAccountInfo.accountName;
          const isSchemeChanged = originalAccountInfo.schemeName !== null && curSchemeId !== originalAccountInfo.schemeName;
          const isAccNoChanged = originalAccountInfo.accountNo !== "" && curAccNo !== originalAccountInfo.accountNo;

          if (isAccNameChanged || isSchemeChanged || isAccNoChanged) {
            alert("Cannot change Scheme Name, Account Name, or Account Number because receipts already exist for this account.");
            return;
          }
        }

        const isDuplicateInSameScheme = checkDuplicateInSameScheme(
          selectedSchemeId,
          selectedAccountId,
          schemeRegData.entryNo
        );

        if (isDuplicateInSameScheme) {
          alert(`This account name is already registered for the selected scheme. Duplicate account names are not allowed within the same scheme.`);
          return;
        }

        const isAccNoDuplicateUpd = regcustomers.some(
          (customer) =>
            String(customer.accno || "").trim().toLowerCase() === String(schemeRegData.accountNo).trim().toLowerCase() &&
            customer.entryno !== schemeRegData.entryNo
        );
        if (isAccNoDuplicateUpd) {
          alert(`Account Number "${schemeRegData.accountNo}" is already in use by another registration.`);
          return;
        }

        const updatedSchemeRegData = {
          ...schemeRegData,
          agent: schemeRegData.agent?.value || 0,
          accountName: schemeRegData.accountName?.value,
          schemeName: schemeRegData.schemeName?.value,
          proofFrontBase64: schemeRegData.proofFrontBase64,
          proofBackBase64: schemeRegData.proofBackBase64,
          proofType: schemeRegData.proofType?.label || schemeRegData.proofType?.value || "",
          isActive: schemeRegData.isActive ? 1 : 0,
          // Send closedDate: today's date when closing, null when re-activating
          closedDate: schemeRegData.isActive ? null : new Date().toISOString().split('T')[0],
        };

        const response = await axios.post(`${apiBaseUrl}/main/updateScheme/${dbCode}`, updatedSchemeRegData);

        if (response.status === 200) {
          alert("Account Updated Successfully!");
          handleClear();
        } else {
          console.error("Error submitting data:", response.data);
          alert("Error updating data. Please try again.");
        }
      }
      await fetchupdschregisteredcustomers();
    } catch (error) {
      console.error("Error:", error.message);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert("An error occurred while updating. Please try again.");
      }
    }
  };

  const handleDelete = async () => {
    try {
      const selectedAccountId = schemeRegData.accountName?.value;
      const accountNo = schemeRegData.accountNo;
      const autoId = schemeRegData.auto;

      if (!selectedAccountId) {
        alert("please select an Account Name");
        return;
      }

      // Check if account has receipts or redemption before prompting delete
      try {
        const checkUrl = `${apiBaseUrl}/main/checkAccountInUse/${dbCode}/${selectedAccountId}?accountNo=${accountNo || ''}&auto=${autoId || ''}`;
        const checkRes = await axios.get(checkUrl);
        if (checkRes.data && checkRes.data.inUse) {
          alert("Cannot delete! Receipts or redemption already exist for this account.");
          return;
        }
      } catch (err) {
        console.error("Error checking account usage:", err);
      }

      if (!window.confirm("Do You Want To Delete..?")) {
        return;
      }

      const response = await axios.delete(
        `${apiBaseUrl}/main/deletebyAccname/${selectedAccountId}/${dbCode}?locationId=${activeLocation ? activeLocation.id : ''}&accountNo=${accountNo || ''}&auto=${autoId || ''}`
      );
      if (response.status === 200 && response.data.success && response.data.affectedRows > 0) {
        alert("Account Deleted Successfully!");
        handleClear();
      } else {
        console.error("Error deleting data:", response.data);
        alert(response.data?.message || "Error deleting data");
      }
      await fetchupdschregisteredcustomers();
    } catch (error) {
      console.error("Error:", error.message);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert("An error occurred while deleting. Please try again.");
      }
    }
  };

  const handleCheckCustomer = (e) => {
    const targetValue = e.target.value;
    const isValuePresent = AcNames.some((item) => item.label === targetValue);

    if (!isValuePresent && e.target.value !== "") {
      alert("Customer Not Registered");
      sessionStorage.setItem("schemeRegAccountNameFocus", "true");
      sessionStorage.setItem("schemeRegTypedAccountName", targetValue);
      Open(e, accountNameInputRef);
    }
  };

  const handleCheckScheme = (e) => {
    const targetValue = e.target.value;
    const matched = schemeNames.find((item) => item.label?.toLowerCase() === targetValue?.toLowerCase());

    if (matched) {
      handleSchemeSelect(matched);
      return;
    }

    if (!matched && e.target.value !== "") {
      alert("Scheme Not Registered");
      sessionStorage.setItem("schemeRegSchemeNameFocus", "true");
      if (OpenSchemeMgmt) {
        OpenSchemeMgmt(e, schemeNameInputRef);
      }
    }
  };

  const proofTypes = [
    { value: "Aadhaar", label: "Aadhaar Card" },
    { value: "VoterID", label: "Voter ID" },
    { value: "PAN", label: "PAN Card" },
    { value: "Driving", label: "Driving License" },
    { value: "Passport", label: "Passport" },
    { value: "Other", label: "Other" },
  ];

  // Toggle function for Active/Closed status
  const toggleAccountStatus = () => {
    setschemeRegData(prev => ({
      ...prev,
      isActive: !prev.isActive,
      // Set closedDate to today when closing; clear when re-activating
      closedDate: prev.isActive ? new Date().toISOString().split('T')[0] : null,
    }));
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-1 font-Inter select-none"
      style={{ zIndex: zIndex || 101 }}
    >
      <div
        className={`bg-white rounded-[10px] shadow-2xl flex flex-col absolute transition-all duration-300 overflow-hidden border border-gray-200 ${minimized ? "minimized" : ""}`}
        style={{ width: 800, minHeight: 440 }}
      >
        {/* Window Header */}
        <WindowHeader
          title="Scheme Customer Registration"
          logo={headeLogo}
          onClose={onClose}
          className="bg-[#1e40af] text-white h-9 shrink-0"
          titleClassName="text-[13px] font-semibold font-Inter"
          closeBtnClassName="h-6 w-6 my-auto mr-1.5 bg-red-600 hover:bg-red-700 rounded-sm"
        />

        {/* Action Toolbar */} 
        <ActionToolbar
          width="100%"
          height="auto"
          size="small"
          iconSize={13}
          bgColor="#f0f4ff"
          navBgColor="#1a3abf"
          navIconColor="#ffffff"
          buttonGap="4px"
          arrowGap="1.5%"
          showSave={true}
          showPrint={true}
          showClear={true}
          showFind={true}
          showEdit={true}
          showDelete={true}
          showExit={true}
          showNavigation={true}
          saveDisabled={saveDisabled}
          editDisabled={editDisabled}
          deleteDisabled={deleteDisabled}
          dataFound={dataFound}
          permissions={permissions}
          permissionKey="schemeReg"
          onSave={handleSubmit}
          onClear={handleClear}
          onFind={handleFind}
          onEdit={handleUpdate}
          onDelete={handleDelete}
          onExit={onClose}
          onFirst={handleFindFirst}
          onPrevious={handleFindprev}
          onNext={handleFindNext}
          onLast={handleFindLast}
          onRefsUpdate={updateNavRefs}
        />

        {/* ── Main Body with vertical structure ─────────────────────────── */}
        <div className="flex-grow p-2 bg-slate-50 overflow-y-auto flex flex-col gap-2 box-border">

          {/* ── TOP CARD: SCHEME INFORMATION ────────────────────────────── */}
          <div className="bg-white p-2 rounded-lg border border-slate-200 shadow-xs flex flex-col gap-1.5 box-border">
            {/* Section Header */}
            <div className="flex items-center gap-2 pb-1 border-b border-dashed border-slate-200 mb-0">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1a3abf"
                strokeWidth="2.5"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              <span className="text-[10.5px] font-bold text-[#1a3abf] uppercase tracking-wider font-Inter">
                Scheme Information
              </span>
            </div>

            {/* Grid structure for columns */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {/* LEFT COLUMN */}
              <div className="flex flex-col gap-2">
                {/* Entry No */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]" htmlFor="entry-no">
                    Entry No
                  </label>
                  <input
                    id="entry-no"
                    ref={entryNoInputRef}
                    readOnly
                    type="text"
                    className="border border-slate-300 rounded-[5px] outline-none px-2 text-[12px] font-bold text-slate-800 bg-[#fef9c3] font-Inter box-border animate-none"
                    style={{ width: "100%", height: 28 }}
                    value={schemeRegData.entryNo}
                    onKeyDown={(e) => handleKeyDown(e, 'entryNo')}
                  />
                </div>

                {/* Scheme Name */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Scheme Name <span className="text-red-500">*</span>
                  </label>
                  <div className="flex-grow">
                    <SherComboBox
                      comboRef={schemeNameInputRef}
                      id="scheme-name"
                      disabled={dataFound && hasReceipts}
                      value={schemeRegData.schemeName?.label || ""}
                      onSelect={(opt) => handleSchemeSelect(opt)}
                      onClear={() => handleSchemeSelect(null)}
                      onFreeInput={(txt) => {
                        setschemeRegData({ ...schemeRegData, schemeName: { value: null, label: txt } });
                      }}
                      onKeyDown={(e) => handleKeyDown(e, 'schemeName')}
                      options={schemeNames}
                      placeholder="Enter scheme name..."
                      showArrow={true}
                      clearable={true}
                      inputHeight={28}
                      inputWidth="100%"
                      inputStyle={{
                        borderRadius: 5,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 11,
                        borderColor: "#cbd5e1",
                      }}
                      onBlur={(e) => handleCheckScheme(e)}
                    />
                  </div>
                </div>

                {/* Scheme Type */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Scheme Type
                  </label>
                  <input
                    type="text"
                    readOnly
                    className="flex-grow border border-slate-300 rounded-[5px] outline-none px-2 text-[12px] font-medium font-Inter box-border bg-slate-50 text-slate-700"
                    style={{ height: 28 }}
                    value={schemeRegData.type || ""}
                    placeholder="Monthly / Daily"
                  />
                </div>

                {/* Account Name */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Account Name
                  </label>
                  <div className="flex-grow">
                    <SherComboBox
                      comboRef={accountNameInputRef}
                      id="account-name"
                      disabled={dataFound && hasReceipts}
                      value={schemeRegData.accountName?.label || ""}
                      onSelect={(opt) => {
                        setschemeRegData({ ...schemeRegData, accountName: opt });
                        fetchcustomerdetails(opt.value);
                      }}
                      onFreeInput={(txt) => {
                        setschemeRegData({ ...schemeRegData, accountName: { value: null, label: txt } });
                      }}
                      onKeyDown={(e) => handleKeyDown(e, 'accountName')}
                      options={AcNames}
                      placeholder="Enter account name..."
                      showArrow={true}
                      clearable={true}
                      inputHeight={28}
                      inputWidth="100%"
                      inputStyle={{
                        borderRadius: 5,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 11,
                        borderColor: "#cbd5e1",
                      }}
                      onBlur={(e) => handleCheckCustomer(e)}
                    />
                  </div>
                </div>

                {/* Agent */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Agent
                  </label>
                  <div className="flex-grow">
                    <SherComboBox
                      comboRef={agentNameInputRef}
                      id="agent-name"
                      value={schemeRegData.agent?.label || ""}
                      onSelect={(opt) => {
                        setschemeRegData({ ...schemeRegData, agent: opt });
                      }}
                      onFreeInput={(txt) => {
                        setschemeRegData({ ...schemeRegData, agent: { value: null, label: txt } });
                      }}
                      onKeyDown={(e) => handleKeyDown(e, 'agent')}
                      options={employeeNames}
                      placeholder="Select agent..."
                      showArrow={true}
                      clearable={true}
                      inputHeight={28}
                      inputWidth="100%"
                      inputStyle={{
                        borderRadius: 5,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 11,
                        borderColor: "#cbd5e1",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN */}
              <div className="flex flex-col gap-2">
                {/* Date */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]" htmlFor="reg-date">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="reg-date"
                    ref={dateInputRef}
                    type="date"
                    min={minDate}
                    max={maxDate}
                    className="flex-grow border border-slate-300 rounded-[5px] outline-none px-2 text-[12px] font-Inter box-border bg-white text-slate-800 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12"
                    style={{ height: 28 }}
                    value={schemeRegData.date}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val && !isDateInFy(val)) {
                        alert("Date incompatible with financial year");
                        return;
                      }
                      setschemeRegData({ ...schemeRegData, date: val });
                    }}
                    onKeyDown={(e) => handleKeyDown(e, 'date')}
                  />
                </div>

                {/* Duration */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]" htmlFor="duration">
                    Duration
                  </label>
                  <div className="flex-grow flex gap-1.5">
                    <input
                      id="duration"
                      ref={durationInputRef}
                      type="number"
                      placeholder="12"
                      className="flex-grow border border-slate-300 rounded-[5px] outline-none px-2 text-[12px] font-Inter box-border bg-white text-slate-800 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12"
                      style={{ height: 28 }}
                      value={schemeRegData.duration}
                      onKeyDown={(e) => handleKeyDown(e, 'duration')}
                      onChange={(e) =>
                        setschemeRegData({
                          ...schemeRegData,
                          duration: e.target.value,
                        })
                      }
                    />
                    <select
                      className="border border-slate-300 rounded-[5px] outline-none px-1 text-[11px] font-Inter bg-slate-50 text-slate-700 w-[75px] h-7"
                      value={schemeRegData.type === "Daily" ? "Days" : "Months"}
                      disabled
                    >
                      <option value="Months">Months</option>
                      <option value="Days">Days</option>
                    </select>
                  </div>
                </div>

                {/* % Perc */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]" htmlFor="int-perc">
                    % Perc
                  </label>
                  <input
                    id="int-perc"
                    ref={intperRef}
                    type="text"
                    placeholder="Enter %"
                    className="flex-grow border border-slate-300 rounded-[5px] outline-none px-2 text-[12px] font-Inter box-border bg-white text-slate-800 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12"
                    style={{ height: 28 }}
                    value={schemeRegData.intperc}
                    onKeyDown={(e) => handleKeyDown(e, 'intperc')}
                    onChange={(e) =>
                      setschemeRegData({
                        ...schemeRegData,
                        intperc: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Account Number */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]" htmlFor="account-no">
                    Account Number
                  </label>
                  <div className="flex-grow flex gap-1.5">
                    <input
                      id="account-no"
                      ref={accountNoInputRef}
                      type="text"
                      disabled={dataFound && hasReceipts}
                      className={`flex-grow border border-slate-300 rounded-[5px] outline-none px-2 text-[12px] font-Inter box-border text-slate-800 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12 ${
                        dataFound && hasReceipts ? "bg-slate-100 cursor-not-allowed text-slate-500" : "bg-white"
                      }`}
                      style={{ height: 28 }}
                      value={schemeRegData.accountNo}
                      onKeyDown={(e) => handleKeyDown(e, 'accountNo')}
                      onChange={(e) =>
                        setschemeRegData({
                          ...schemeRegData,
                          accountNo: e.target.value,
                        })
                      }
                    />
                    <button
                      type="button"
                      onClick={handleSearchByAccountNo}
                      className="h-7 px-3 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 text-[10.5px] font-bold rounded shadow-3xs cursor-pointer flex items-center justify-center font-Inter"
                    >
                      Search
                    </button>
                  </div>
                </div>

                {/* Status Switcher */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Status
                  </label>
                  <div className="flex-grow flex items-center gap-2">
                    <div className="relative inline-block w-9 align-middle select-none">
                      <input
                        type="checkbox"
                        name="accountStatus"
                        id="accountStatus"
                        checked={schemeRegData.isActive}
                        onChange={toggleAccountStatus}
                        className="sr-only peer"
                      />
                      <label
                        htmlFor="accountStatus"
                        className={`block overflow-hidden h-5 rounded-full cursor-pointer transition-colors duration-300 ${
                          schemeRegData.isActive ? 'bg-[#10b981]' : 'bg-[#ef4444]'
                        }`}
                      >
                        <span
                          className={`block h-4 w-4 mt-0.5 ml-0.5 rounded-full bg-white border border-slate-200 shadow-xs transform transition-transform duration-300 ease-in-out ${
                            schemeRegData.isActive ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </label>
                    </div>
                    <span className={`text-[11px] font-semibold ${schemeRegData.isActive ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
                      Active
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── BOTTOM CARD: ADDRESS & NOMINEE DETAILS TABS ───────────────── */}
          <div className="bg-white p-2 rounded-lg border border-slate-200 shadow-xs flex flex-col gap-1.5 box-border">
            {/* Tabs Header */}
            <div className="flex gap-4 border-b border-slate-200 pb-1">
              {/* Address Tab wrapper */}
              <div 
                className={`flex flex-col items-center pb-1 -mb-[10px] border-b-2 transition-all duration-150 ${
                  activeTab === 'address' ? 'border-[#0f34ac]' : 'border-transparent'
                }`}
              >
                <button
                  ref={addressTabRef}
                  type="button"
                  className={`px-3.5 py-1 text-[11px] font-bold font-Inter flex items-center gap-1.5 rounded transition-all duration-150 cursor-pointer ${
                    activeTab === 'address'
                      ? 'bg-[#0f34ac] text-white shadow-3xs'
                      : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                  onClick={() => {
                    setActiveTab('address');
                    setTimeout(() => {
                      if (address1InputRef.current) address1InputRef.current.focus();
                    }, 50);
                  }}
                  onKeyDown={(e) => handleKeyDown(e, 'tab-address')}
                >
                  <svg width="10.5" height="10.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  Address
                </button>
              </div>

              {/* Nominee Tab wrapper */}
              <div 
                className={`flex flex-col items-center pb-1 -mb-[10px] border-b-2 transition-all duration-150 ${
                  activeTab === 'nominee' ? 'border-[#0f34ac]' : 'border-transparent'
                }`}
              >
                <button
                  ref={nomineeTabRef}
                  type="button"
                  className={`px-3.5 py-1 text-[11px] font-bold font-Inter flex items-center gap-1.5 rounded transition-all duration-150 cursor-pointer ${
                    activeTab === 'nominee'
                      ? 'bg-[#0f34ac] text-white shadow-3xs'
                      : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                  onClick={() => {
                    setActiveTab('nominee');
                    setTimeout(() => {
                      if (nomineeNameInputRef.current) nomineeNameInputRef.current.focus();
                    }, 50);
                  }}
                  onKeyDown={(e) => handleKeyDown(e, 'tab-nominee')}
                >
                  <svg width="10.5" height="10.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  Nominee
                </button>
              </div>
            </div>

            {/* TAB CONTENT: ADDRESS */}
            {activeTab === 'address' && (
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                {/* Address 1 */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Address 1
                  </label>
                  <input
                    ref={address1InputRef}
                    value={schemeRegData.add1}
                    onKeyDown={(e) => handleKeyDown(e, 'address1')}
                    onChange={(e) => setschemeRegData({ ...schemeRegData, add1: e.target.value })}
                    placeholder="Enter address line 1..."
                    className="flex-grow h-7 px-2 text-[11.5px] border border-slate-300 rounded-[5px] outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 font-Inter bg-white"
                  />
                </div>

                {/* Address 2 */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Address 2
                  </label>
                  <input
                    ref={address2InputRef}
                    value={schemeRegData.add2}
                    onKeyDown={(e) => handleKeyDown(e, 'address2')}
                    onChange={(e) => setschemeRegData({ ...schemeRegData, add2: e.target.value })}
                    placeholder="Enter address line 2..."
                    className="flex-grow h-7 px-2 text-[11.5px] border border-slate-300 rounded-[5px] outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 font-Inter bg-white"
                  />
                </div>

                {/* City/Town */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    City/Town
                  </label>
                  <input
                    ref={cityInputRef}
                    value={schemeRegData.city}
                    onKeyDown={(e) => handleKeyDown(e, 'city')}
                    onChange={(e) => setschemeRegData({ ...schemeRegData, city: e.target.value })}
                    placeholder="Enter city/town..."
                    className="flex-grow h-7 px-2 text-[11.5px] border border-slate-300 rounded-[5px] outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 font-Inter bg-white"
                  />
                </div>

                {/* Pincode */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Pincode
                  </label>
                  <input
                    ref={pincodeInputRef}
                    type="number"
                    value={schemeRegData.pincode}
                    onKeyDown={(e) => handleKeyDown(e, 'pincode')}
                    onChange={(e) => setschemeRegData({ ...schemeRegData, pincode: e.target.value })}
                    placeholder="Enter pincode..."
                    className="flex-grow h-7 px-2 text-[11.5px] border border-slate-300 rounded-[5px] outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 font-Inter bg-white"
                  />
                </div>

                {/* Contact Person */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Contact Person
                  </label>
                  <input
                    ref={contactPersonInputRef}
                    value={schemeRegData.contactPerson}
                    onKeyDown={(e) => handleKeyDown(e, 'contactPerson')}
                    onChange={(e) => setschemeRegData({ ...schemeRegData, contactPerson: e.target.value })}
                    placeholder="Enter contact person..."
                    className="flex-grow h-7 px-2 text-[11.5px] border border-slate-300 rounded-[5px] outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 font-Inter bg-white"
                  />
                </div>

                {/* Email */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Email
                  </label>
                  <input
                    ref={emailInputRef}
                    type="email"
                    value={schemeRegData.email}
                    onKeyDown={(e) => handleKeyDown(e, 'email')}
                    onChange={(e) => setschemeRegData({ ...schemeRegData, email: e.target.value })}
                    placeholder="Enter email address..."
                    className="flex-grow h-7 px-2 text-[11.5px] border border-slate-300 rounded-[5px] outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 font-Inter bg-white"
                  />
                </div>

                {/* Tele No. */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Tele No.
                  </label>
                  <input
                    ref={telNoInputRef}
                    type="number"
                    value={schemeRegData.tele}
                    onKeyDown={(e) => handleKeyDown(e, 'telNo')}
                    onChange={(e) => setschemeRegData({ ...schemeRegData, tele: e.target.value })}
                    placeholder="Enter telephone number..."
                    className="flex-grow h-7 px-2 text-[11.5px] border border-slate-300 rounded-[5px] outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 font-Inter bg-white"
                  />
                </div>

                {/* Mobile */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Mobile
                  </label>
                  <input
                    ref={mobNoInputRef}
                    type="number"
                    value={schemeRegData.mobile}
                    onKeyDown={(e) => handleKeyDown(e, 'mobNo')}
                    onChange={(e) => setschemeRegData({ ...schemeRegData, mobile: e.target.value })}
                    placeholder="Enter mobile number..."
                    className="flex-grow h-7 px-2 text-[11.5px] border border-slate-300 rounded-[5px] outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 font-Inter bg-white"
                  />
                </div>
              </div>
            )}

            {/* TAB CONTENT: NOMINEE */}
            {activeTab === 'nominee' && (
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                {/* Nominee Name */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Nominee Name
                  </label>
                  <input
                    ref={nomineeNameInputRef}
                    value={schemeRegData.nomineeName}
                    onKeyDown={(e) => handleKeyDown(e, 'nomineeName')}
                    onChange={(e) => setschemeRegData({ ...schemeRegData, nomineeName: e.target.value })}
                    placeholder="Enter full name..."
                    className="flex-grow h-7 px-2 text-[11.5px] border border-slate-300 rounded-[5px] outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 font-Inter bg-white"
                  />
                </div>

                {/* Relation */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Relation
                  </label>
                  <input
                    ref={relationInputRef}
                    value={schemeRegData.relation}
                    onKeyDown={(e) => handleKeyDown(e, 'relation')}
                    onChange={(e) => setschemeRegData({ ...schemeRegData, relation: e.target.value })}
                    placeholder="e.g. Spouse / Son"
                    className="flex-grow h-7 px-2 text-[11.5px] border border-slate-300 rounded-[5px] outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 font-Inter bg-white"
                  />
                </div>

                {/* Proof Type */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Proof Type
                  </label>
                  <div className="flex-grow">
                    <SherComboBox
                      comboRef={proofTypeInputRef}
                      id="proof-type"
                      value={schemeRegData.proofType?.label || ""}
                      onSelect={(opt) => {
                        setschemeRegData({ ...schemeRegData, proofType: opt });
                      }}
                      onFreeInput={(txt) => {
                        setschemeRegData({ ...schemeRegData, proofType: { value: null, label: txt } });
                      }}
                      onKeyDown={(e) => handleKeyDown(e, 'proofType')}
                      options={proofTypes}
                      placeholder="Select type..."
                      showArrow={true}
                      clearable={true}
                      inputHeight={28}
                      inputWidth="100%"
                      inputStyle={{
                        borderRadius: 5,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 11,
                        borderColor: "#cbd5e1",
                      }}
                    />
                  </div>
                </div>

                {/* ID Number */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    ID Number
                  </label>
                  <input
                    ref={idNumberInputRef}
                    value={schemeRegData.idNumber}
                    onKeyDown={(e) => handleKeyDown(e, 'idNumber')}
                    onChange={(e) => setschemeRegData({ ...schemeRegData, idNumber: e.target.value })}
                    placeholder="Enter document number..."
                    className="flex-grow h-7 px-2 text-[11.5px] border border-slate-300 rounded-[5px] outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 font-Inter bg-white"
                  />
                </div>

                {/* Nominee Address 1 */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Address 1
                  </label>
                  <input
                    ref={nomineeAddress1InputRef}
                    value={schemeRegData.nomineeAdd1}
                    onKeyDown={(e) => handleKeyDown(e, 'nomineeAddress1')}
                    onChange={(e) => setschemeRegData({ ...schemeRegData, nomineeAdd1: e.target.value })}
                    placeholder="Nominee Address Line 1"
                    className="flex-grow h-7 px-2 text-[11.5px] border border-slate-300 rounded-[5px] outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 font-Inter bg-white"
                  />
                </div>

                {/* Nominee Address 2 */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Address 2
                  </label>
                  <input
                    ref={nomineeAddress2InputRef}
                    value={schemeRegData.nomineeAdd2}
                    onKeyDown={(e) => handleKeyDown(e, 'nomineeAddress2')}
                    onChange={(e) => setschemeRegData({ ...schemeRegData, nomineeAdd2: e.target.value })}
                    placeholder="Nominee Address Line 2"
                    className="flex-grow h-7 px-2 text-[11.5px] border border-slate-300 rounded-[5px] outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-500 font-Inter bg-white"
                  />
                </div>

                {/* Front Image Upload */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Front Image
                  </label>
                  <div className="flex-grow flex items-center gap-2">
                    <input
                      type="file"
                      className="hidden"
                      ref={proofFrontInputRef}
                      onChange={(e) => handleFileChange(e, 'front')}
                      accept=".jpg,.jpeg,.png,.pdf"
                    />
                    <button
                      ref={proofFrontBrowseRef}
                      type="button"
                      className="h-7 px-2.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 text-[10.5px] font-bold rounded cursor-pointer font-Inter"
                      onClick={() => proofFrontInputRef.current.click()}
                      onKeyDown={(e) => handleKeyDown(e, 'proofFrontBrowse')}
                    >
                      Browse
                    </button>
                    {(schemeRegData.proofFrontFileName || schemeRegData.proofFrontBase64) && (
                      <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded p-0.5">
                        <button type="button" className="text-blue-600 hover:text-blue-800 text-[10px] px-1" onClick={() => showFilePreview('front')} title="Preview">👁️</button>
                        <button type="button" className="text-emerald-600 hover:text-emerald-800 text-[10px] px-1" onClick={() => downloadFile('front')} title="Download">⬇️</button>
                        <button type="button" className="text-rose-500 hover:text-rose-700 text-[10px] px-1 font-bold" onClick={() => handleRemoveFile('front')} title="Remove">✕</button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Back Image Upload */}
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]">
                    Back Image
                  </label>
                  <div className="flex-grow flex items-center gap-2">
                    <input
                      type="file"
                      className="hidden"
                      ref={proofBackInputRef}
                      onChange={(e) => handleFileChange(e, 'back')}
                      accept=".jpg,.jpeg,.png,.pdf"
                    />
                    <button
                      ref={proofBackBrowseRef}
                      type="button"
                      className="h-7 px-2.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 text-[10.5px] font-bold rounded cursor-pointer font-Inter"
                      onClick={() => proofBackInputRef.current.click()}
                      onKeyDown={(e) => handleKeyDown(e, 'proofBackBrowse')}
                    >
                      Browse
                    </button>
                    {(schemeRegData.proofBackFileName || schemeRegData.proofBackBase64) && (
                      <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded p-0.5">
                        <button type="button" className="text-blue-600 hover:text-blue-800 text-[10px] px-1" onClick={() => showFilePreview('back')} title="Preview">👁️</button>
                        <button type="button" className="text-emerald-600 hover:text-emerald-800 text-[10px] px-1" onClick={() => downloadFile('back')} title="Download">⬇️</button>
                        <button type="button" className="text-rose-500 hover:text-rose-700 text-[10px] px-1 font-bold" onClick={() => handleRemoveFile('back')} title="Remove">✕</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Narration (Outside of Tabs Grid but inside bottom card container) */}
            <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
              <label className="text-[11px] font-semibold text-slate-600 font-Inter whitespace-nowrap shrink-0 w-[90px]" htmlFor="narration">
                Narration
              </label>
              <textarea
                id="narration"
                ref={narrationInputRef}
                rows="1.5"
                placeholder="Enter any additional information about this scheme..."
                className="flex-grow border border-slate-300 rounded-[5px] outline-none px-2 py-1 text-[11.5px] font-Inter box-border bg-white text-slate-800 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12 resize-none animate-none"
                value={schemeRegData.narration}
                onKeyDown={(e) => handleKeyDown(e, 'narration')}
                onChange={(e) =>
                  setschemeRegData({
                    ...schemeRegData,
                    narration: e.target.value,
                  })
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SchemeRegistration;